import classnames from 'classnames'
import { ReactNode } from 'react'

import { REVEAL_TIME_MS } from '../../constants/settings'
import { getStoredIsHighContrastMode } from '../../lib/localStorage'
import { CharStatus } from '../../lib/statuses'

type Props = {
  children?: ReactNode
  value: string
  width?: number
  status?: CharStatus
  onClick: (value: string) => void
  isRevealing?: boolean
  solution: string
}

export const Key = ({
  children,
  status,
  width = 40,
  value,
  onClick,
  isRevealing,
  solution,
}: Props) => {
  const keyDelayMs = REVEAL_TIME_MS * solution.length
  const isHighContrast = getStoredIsHighContrastMode()

  const classes = classnames(
    'xxshort:h-8 xxshort:w-8 xxshort:text-xxs xshort:w-10 xshort:h-10 flex short:h-12 h-14 items-center justify-center rounded-lg mx-0.5 text-xs font-bold cursor-pointer select-none transition-all duration-150',
    {
      'bg-slate-200 hover:bg-slate-300 hover:-translate-y-0.5 hover:shadow-[0_4px_0_#94a3b8] active:translate-y-0 active:shadow-[0_1px_0_#94a3b8] shadow-[0_3px_0_#cbd5e1]': !status,
      'bg-slate-400 text-white shadow-[0_3px_0_#94a3b8]': status === 'absent',
      'bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white shadow-[0_3px_0_#ea580c] hover:-translate-y-0.5 hover:shadow-[0_4px_0_#c2410c] active:translate-y-0 active:shadow-[0_1px_0_#c2410c]':
        status === 'correct' && isHighContrast,
      'bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700 text-white shadow-[0_3px_0_#0891b2] hover:-translate-y-0.5 hover:shadow-[0_4px_0_#0e7490] active:translate-y-0 active:shadow-[0_1px_0_#0e7490]':
        status === 'present' && isHighContrast,
      'bg-green-500 hover:bg-green-600 active:bg-green-700 text-white shadow-[0_3px_0_#16a34a] hover:-translate-y-0.5 hover:shadow-[0_4px_0_#15803d] active:translate-y-0 active:shadow-[0_1px_0_#15803d]':
        status === 'correct' && !isHighContrast,
      'bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700 text-white shadow-[0_3px_0_#ca8a04] hover:-translate-y-0.5 hover:shadow-[0_4px_0_#a16207] active:translate-y-0 active:shadow-[0_1px_0_#a16207]':
        status === 'present' && !isHighContrast,
    }
  )

  const styles = {
    transitionDelay: isRevealing ? `${keyDelayMs}ms` : 'unset',
    width: `${width}px`,
  }

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    onClick(value)
    event.currentTarget.blur()
  }

  return (
    <button
      style={styles}
      aria-label={`${value}${status ? ' ' + status : ''}`}
      className={classes}
      onClick={handleClick}
    >
      {children || value}
    </button>
  )
}
